<template>
    <div v-if="!loading">
        <fieldset v-if="!pedidoEmAnalise && !somenteLeitura" class="tipo-acao">
            <legend>O que deseja fazer com o projeto?</legend>
            <label class="opcao-tipo" :class="{ 'opcao-tipo--indisponivel': !podeSolicitarConclusao }"
                :title="!podeSolicitarConclusao ? tituloConclusaoIndisponivel : ''">
                <input type="radio" v-model="tipoAcao" value="conclusao"
                    :disabled="!!solicitação.status || !podeSolicitarConclusao">
                Concluir projeto
            </label>
            <label class="opcao-tipo">
                <input type="radio" v-model="tipoAcao" value="cancelamento" :disabled="!!solicitação.status">
                Cancelar projeto
            </label>
        </fieldset>

        <div v-if="!pedidoEmAnalise && !podeSolicitarConclusao" class="aviso-entregas-pendentes margem" role="status">
            <p>
                Não é possível solicitar a conclusão do projeto enquanto existirem pacotes de trabalho não concluídos.
                Neste caso, você pode solicitar <strong>somente o cancelamento</strong> do projeto.
            </p>
            <ul>
                <li v-for="entrega in entregasImpedemConclusao" :key="entrega.id">
                    {{ entrega.nome }} — {{ entrega.status || 'Sem status' }}
                </li>
            </ul>
        </div>

        <p v-if="pedidoEmAnalise" class="chip alinha-centro margem"
            :class="tipoAcao === 'cancelamento' ? 'bg-erro' : 'bg-cinza'">
            Pedido de {{ tipoAcao === 'cancelamento' ? 'cancelamento' : 'conclusão' }} —
            {{ solicitação.status }}
        </p>

        <fieldset>
            <div class="grid-2">
                <template v-if="tipoAcao === 'conclusao'">
                    <div class="grid-2 col-2">
                        <div>
                            <label>Custo previsto</label>
                            <money3 v-bind="config" v-model="solicitação.custoEstimado" v-if="podeVerOrcamento" readonly
                                :class="{ 'vazio': !solicitação.custoEstimado && !propEstimativaCusto }"
                                class="moneyInput" />
                            <input v-else type="text" value="●●●●●" readonly>
                        </div>
                        <div>
                            <label>Custo Real</label>
                            <money3 v-bind="config" v-model="solicitação.custoReal" class="moneyInput"
                                v-if="podeVerOrcamento" readonly
                                :class="{ 'vazio': solicitação.custoReal == null || solicitação.custoReal === '' }" />
                            <input v-else type="text" value="●●●●●" readonly>
                        </div>
                    </div>
                    <div class="col-2">
                        <label>Evidências de alcance</label>
                        <textarea v-model="solicitação.evidenciaAlcance" :disabled="camposSomenteLeitura"
                            :required="!solicitação.evidenciaAlcance"></textarea>
                    </div>
                    <div class="col-2">
                        <label>Observação</label>
                        <textarea v-model="solicitação.escopoAlcancado" :disabled="camposSomenteLeitura"
                            placeholder="Informações adicionais, se necessário"></textarea>
                    </div>
                </template>
                <template v-else>
                    <div class="col-2">
                        <label>Motivo do cancelamento</label>
                        <textarea v-model="solicitação.conclusao" :disabled="camposSomenteLeitura"
                            :required="!solicitação.conclusao"
                            placeholder="Descreva o motivo do cancelamento do projeto"></textarea>
                    </div>
                    <div class="col-2">
                        <label>Observações complementares (opcional)</label>
                        <textarea v-model="solicitação.evidenciaAlcance" :disabled="camposSomenteLeitura"
                            placeholder="Informações adicionais, se necessário"></textarea>
                    </div>
                </template>
            </div>
        </fieldset>
<br>
        <div v-if="!somenteLeitura" class="alinha-centro acoes">
            <button v-if="!solicitação.status" @click="solicitarPedido()" :disabled="composVazios">
                {{ tipoAcao === 'cancelamento' ? 'Solicitar cancelamento do projeto' : 'Solicitar conclusão do projeto' }}
            </button>
            <template v-else-if="solicitação.status === 'Aguardando aprovação'">
                <button type="button" class="acao-secundaria" @click="desistirPedido()">
                    Desistir do pedido
                </button>
                <button class="acao-secundaria aprovar" @click="aprovarEncerramento(solicitação.projeto_id, 'Aprovado')">
                    {{ tipoAcao === 'cancelamento' ? 'Aprovar cancelamento' : 'Aprovar conclusão' }}
                </button>
                <button class="acao-secundaria reprovar"
                    @click="aprovarEncerramento(solicitação.projeto_id, 'Reprovado')">
                    Reprovar
                </button>
            </template>
            <button v-else-if="solicitação.status === 'Aprovado'" class="acao-secundaria"
                :class="tipoAcao === 'cancelamento' ? 'reprovar' : 'aprovar'" disabled>
                {{ tipoAcao === 'cancelamento' ? 'Cancelamento aprovado' : 'Conclusão aprovada' }}
            </button>
            <button v-else class="acao-secundaria" disabled>
                Pedido {{ solicitação.status?.toLowerCase() }}
            </button>
        </div>
    </div>
    <div v-else class="loading">
        <div></div>
    </div>
</template>

<script>
import serviceProjetos from "@/services/serviceProjetos";
import { Money3Component } from "v-money3";

export default {
    name: 'EncerramentoProjeto',
    components: { money3: Money3Component },

    emits: ['update:modelValue'],

    props: {
        propProjetoID: {
            required: true
        },
        podeVerOrcamento: {
            required: true
        },
        propEstimativaCusto: {
            required: true
        },
        modelValue: {
            type: Boolean,
            default: false,
        },
        entregas: {
            type: Array,
            default: () => [],
        },
        somenteLeitura: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        entregasImpedemConclusao() {
            return (this.entregas || []).filter((entrega) => {
                const status = (entrega?.status || '').trim()
                return status !== 'Concluído' && status !== 'Cancelada'
            })
        },
        podeSolicitarConclusao() {
            return this.entregasImpedemConclusao.length === 0
        },
        tituloConclusaoIndisponivel() {
            if (this.podeSolicitarConclusao) return ''
            const n = this.entregasImpedemConclusao.length
            return n === 1
                ? 'Há 1 pacote de trabalho não concluído. Solicite apenas cancelamento ou conclua o pacote de trabalho antes.'
                : `Há ${n} pacotes de trabalho não concluídos. Solicite apenas cancelamento ou conclua os pacotes de trabalho antes.`
        },
        pedidoEmAnalise() {
            return !!this.solicitação.status
        },
        camposSomenteLeitura() {
            return this.somenteLeitura || !!this.solicitação.status
        },
        composVazios() {
            if (this.tipoAcao === 'cancelamento') {
                return !this.solicitação.conclusao?.trim()
            }

            const custoEstimado = this.solicitação.custoEstimado ?? this.propEstimativaCusto
            return (
                !this.solicitação.evidenciaAlcance?.trim() ||
                custoEstimado == null || custoEstimado === '' ||
                this.solicitação.custoReal == null || this.solicitação.custoReal === ''
            )
        }
    },
    data() {
        return {
            tipoAcao: 'conclusao',
            solicitação: {
                custoEstimado: this.propEstimativaCusto
            },
            loading: true,
            config: {
                masked: false,
                prefix: "R$ ",
                thousands: ".",
                decimal: ",",
                precision: 2,
            },
        }
    },
    created() {
        this.getEncerramentos()
    },
    watch: {
        loading(v) {
            if (!v) this.emitExistePedidoEncerramento()
        },
        solicitação: {
            deep: true,
            handler() {
                if (!this.loading) this.emitExistePedidoEncerramento()
            },
        },
        tipoAcao(novo, antigo) {
            if (this.solicitação.status || novo === antigo) return
            this.solicitação = {
                custoEstimado: this.propEstimativaCusto,
            }
            if (novo === 'conclusao') {
                this.preencherCustoRealAutomatico()
            }
        },
        propProjetoID(novo, antigo) {
            if (!novo || novo === antigo || this.solicitação.status) return
            this.preencherCustoRealAutomatico()
        },
        propEstimativaCusto(valor) {
            if (this.solicitação.status) return
            if (this.solicitação.custoEstimado == null || this.solicitação.custoEstimado === '') {
                this.solicitação.custoEstimado = valor
            }
        },
        podeSolicitarConclusao(pode) {
            if (!pode && !this.solicitação.status && this.tipoAcao === 'conclusao') {
                this.tipoAcao = 'cancelamento'
            }
        },
    },
    methods: {
        emitExistePedidoEncerramento() {
            const existe = !!this.solicitação.status
            if (existe !== this.modelValue) {
                this.$emit('update:modelValue', existe)
            }
        },
        novaSolicitacaoVazia() {
            return {
                custoEstimado: this.propEstimativaCusto,
            }
        },
        async aprovarEncerramento(id, status) {
            if (this.somenteLeitura) return
            await serviceProjetos.aprovarEncerramento(id, status);
            this.getEncerramentos()
        },
        async getEncerramentos() {
            this.loading = true;
            try {
                const response = await serviceProjetos.getEncerramentos();
                const lista = Array.isArray(response) ? response : [];
                const pedido = lista.find(i => i.projeto_id == this.propProjetoID) ?? null
                if (pedido) {
                    pedido.tipo = pedido.tipo || 'conclusao';
                    this.tipoAcao = pedido.tipo;
                    this.solicitação = pedido;
                } else {
                    this.tipoAcao = this.podeSolicitarConclusao ? 'conclusao' : 'cancelamento';
                    this.solicitação = this.novaSolicitacaoVazia();
                }
                await this.preencherCustoRealAutomatico();
            } catch {
                this.tipoAcao = this.podeSolicitarConclusao ? 'conclusao' : 'cancelamento';
                this.solicitação = this.novaSolicitacaoVazia();
                await this.preencherCustoRealAutomatico();
            } finally {
                this.loading = false;
            }
        },
        async preencherCustoRealAutomatico() {
            if (!this.propProjetoID || this.solicitação.status) return
            if (this.tipoAcao !== 'conclusao') return

            try {
                const custo = await serviceProjetos.getCustoRealProjeto(this.propProjetoID)
                if (custo != null && !Number.isNaN(Number(custo))) {
                    this.solicitação.custoReal = Number(custo)
                }
            } catch {
                // Mantém campo vazio se a API falhar
            }
        },
        async solicitarPedido() {
            if (this.somenteLeitura) return
            if (this.tipoAcao === 'conclusao' && !this.podeSolicitarConclusao) {
                window.alert(
                    'Não é possível solicitar a conclusão do projeto enquanto houver pacotes de trabalho não concluídos. '
                    + 'Conclua todos os pacotes de trabalho ou solicite o cancelamento do projeto.',
                )
                return
            }
            const payload = {
                projeto_id: this.propProjetoID,
                tipo: this.tipoAcao,
                evidenciaAlcance: this.solicitação.evidenciaAlcance,
                custoEstimado: this.solicitação.custoEstimado ?? this.propEstimativaCusto,
                custoReal: this.solicitação.custoReal,
                conclusao: this.solicitação.conclusao,
                resultadoObtido: this.solicitação.resultadoObtido,
                status: 'Aguardando aprovação',
            }
            if (this.tipoAcao === 'conclusao') {
                payload.escopoAlcancado = this.solicitação.escopoAlcancado
            }
            await serviceProjetos.solicitarEncerramento(payload);
            await this.getEncerramentos();
        },
        async desistirPedido() {
            if (this.somenteLeitura) return
            if (!confirm('Deseja desistir deste pedido? Você poderá solicitar conclusão ou cancelamento novamente.')) {
                return
            }
            await serviceProjetos.desistirPedidoEncerramento(this.propProjetoID);
            this.tipoAcao = this.podeSolicitarConclusao ? 'conclusao' : 'cancelamento';
            this.solicitação = this.novaSolicitacaoVazia();
            await this.getEncerramentos();
        },
    },
}
</script>

<style scoped>
.tipo-acao {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
    align-items: center;
    border: none;
    /* padding: 0 0 1rem; */
}

.tipo-acao legend {
    width: 100%;
    font-weight: 600;
    /* margin-bottom: 0.25rem; */
}

.opcao-tipo {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
}

.opcao-tipo--indisponivel {
    opacity: 0.55;
    cursor: not-allowed;
}

.aviso-entregas-pendentes {
    padding: 0.85rem 1rem;
    border-radius: 8px;
    background: rgba(255, 145, 0, 0.12);
    border: 1px solid rgba(255, 145, 0, 0.35);
    font-size: 0.92rem;
}

.aviso-entregas-pendentes p {
    margin: 0 0 0.5rem;
}

.aviso-entregas-pendentes ul {
    margin: 0;
    padding-left: 1.25rem;
}

.acoes {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    justify-content: center;
}

.moneyInput {
    border: 1px solid var(--cor-separador);
    font-size: 16px;
    border-radius: 6px;
    height: 48px;
    padding: 12px 16px;
    width: 100%;
}

.vazio {
    box-shadow: 0 -1px var(--cor-erro) inset
}

.reprovar {
    background-color: var(--cor-erro);
    color: var(--cor-bg);
}

.aprovar {
    background-color: var(--cor-sucesso);
    color: var(--cor-bg);
}

:disabled {
    cursor: not-allowed;
}
</style>
